import React from 'react';
import { DEFAULT_MEDIA_SETTINGS, SettingsAPI } from '../../api/settings';
import { SwitchField } from './ReviewerSettingsSection';

/** What the one small vision call per upload buys, and what it costs. */
const AUTO_DESCRIBE_HELP =
  'generators and the reviewer are told what an image shows (one small vision call per image, once)';

/**
 * The account's media handling, next to the reviewer settings the describing
 * feeds: an uploaded image nothing has read is a reference the generators and
 * the judge are blind to.
 *
 * The switch IS the save — a single account-level boolean is not worth a form —
 * so it moves at once and is put back if the write is refused. There is no
 * pipeline-level override: this is about what happens to a file when it lands,
 * long before any pipeline reads it.
 */
const MediaSettingsSection: React.FC = () => {
  const [autoDescribe, setAutoDescribe] = React.useState(
    DEFAULT_MEDIA_SETTINGS.auto_describe_uploads
  );
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    void SettingsAPI.getMedia()
      .then((media) => {
        if (cancelled) return;
        setAutoDescribe(media.auto_describe_uploads);
        setNotice('');
      })
      // Never saved is the normal case and reads as the default; a failed read
      // is not, so the pane says the switch is showing the default rather than
      // the account's own answer.
      .catch(() => {
        if (!cancelled) setNotice('Could not read this setting — showing the default.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async () => {
    const previous = autoDescribe;
    const next = !previous;
    setAutoDescribe(next);
    setNotice('');
    setSaving(true);
    try {
      const saved = await SettingsAPI.putMedia({ auto_describe_uploads: next });
      setAutoDescribe(saved.auto_describe_uploads);
    } catch {
      setAutoDescribe(previous);
      setNotice('Could not save that — the setting is unchanged.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-4 border-t border-[var(--border-subtle)] pt-4 max-w-md"
      data-testid="media-settings"
    >
      <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">
        Uploads
      </h4>
      <p className="text-[11px] text-[var(--app-text-muted)] mb-3">Saved as you switch it.</p>

      <div className="flex flex-col gap-2">
        <SwitchField
          label="Describe uploads automatically"
          checked={autoDescribe}
          disabled={saving}
          onToggle={() => void toggle()}
        />
        <p className="text-[11px] text-[var(--app-text-muted)]">{AUTO_DESCRIBE_HELP}</p>
        {notice && (
          <p className="text-xs text-amber-400" data-testid="media-settings-notice">
            {notice}
          </p>
        )}
      </div>
    </div>
  );
};

export default MediaSettingsSection;
